import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { Type } from '../../models/Type'
import { User } from '../../models/User'
import { ComponentData } from '../../utils/typing'
import { Icon, IconName } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Label from '../app/design/Label'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { ActionButton } from '../common/ListHeader'
import { ShareLinkModal } from '../common/modals/ShareLinkModal'
import { MoreMenuButton } from '../common/MoreMenuButton'
import { EmptyDashboardLayout } from '../dashboards/EmptyDashboardLayout'
import { SettingsLayout } from './SettingsLayout'

const viewWillAppear = async (base: PocketComponent) => {
    return { forms: await RecordFactory.getInstance().loadTypes(base.owner.id!) }
}

function FormIcon({ item }: { item: Type }) {
    if (item.image?.url) {
        return <img src={item.image.url} alt={item.name} className="size-9 rounded-full" />
    }
    return (
        <Icon
            icon={`field.small`}
            shouldInheritColor
            background="round"
            className="text-primary-700 size-9"
        />
    )
}

function FormMoreMenu({ form, user }: { form: Type; user: User }) {
    const navigate = useNavigate()
    const [showShareLinkModal, setShowShareLinkModal] = useState(false)

    const options = (() => {
        const a = [
            {
                id: 'edit',
                title: 'Edit form',
                icon: 'pencil' as IconName,
            },
            {
                id: 'copy',
                title: 'Copy form',
                icon: 'layers' as IconName,
            },
        ]

        if (user.allowWidget) {
            a.push({
                id: 'share',
                title: 'Share form link',
                icon: 'share' as IconName,
            })
        }

        return a
    })()

    return (
        <>
            <div className="flex w-full justify-end">
                <MoreMenuButton
                    align="end"
                    options={options}
                    onSelectOption={(o) => {
                        switch (o.id) {
                            case 'edit':
                                navigate(`/settings/forms/${form.id}/edit`)
                                break
                            case 'share':
                                setShowShareLinkModal(true)
                                break
                            case 'copy':
                                navigate(`/settings/forms/${form.id}/copy`)
                                break
                        }
                    }}
                />
            </div>
            <ShareLinkModal
                uri={`/form/${form.key}`}
                isOpen={showShareLinkModal}
                onClose={() => setShowShareLinkModal(false)}
            />
        </>
    )
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    return (
        <SettingsLayout
            type="form"
            actionButton={{
                buttonLabel: `Add a form`,
                actions: [
                    {
                        id: 'new',
                        label: `Create a new form`,
                        href: `/settings/forms/new`,
                        icon: 'field',
                    },
                    {
                        id: 'defaults',
                        label: `Add a form from defaults`,
                        href: `/settings/forms/defaults`,
                        icon: 'documentPen',
                    },
                ],
                icon: 'plus.small',
                intent: 'primary',
            }}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.forms.map((item) => (
                        <ClickableTableRow
                            key={item.id ?? ''}
                            href={`/settings/forms/${item.id}/edit`}
                        >
                            <TableCell>
                                <div className="flex items-center gap-x-4">
                                    <FormIcon item={item} />
                                    <Label size="sm">{item.name}</Label>
                                </div>
                            </TableCell>

                            <TableCell>
                                <FormMoreMenu form={item} user={base.me} />
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {data.forms.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout
                                dashboard={
                                    new Dashboard({
                                        title: `No forms`,
                                    })
                                }
                                searchtext={''}
                                actionButton={
                                    <ActionButton
                                        actionButton={{
                                            buttonLabel: `Add a form`,
                                            icon: 'plus.small',
                                            actions: [
                                                {
                                                    id: 'new',
                                                    label: `Create a new form`,
                                                    href: `/settings/forms/new`,
                                                },
                                                {
                                                    id: 'defaults',
                                                    label: `Add a form from defaults`,
                                                    href: `/settings/forms/defaults`,
                                                },
                                            ],
                                            intent: 'secondary',
                                        }}
                                    />
                                }
                            />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </SettingsLayout>
    )
}

export const SetupTypesPage = usePSComponent(render, viewWillAppear)
